/**
 * MemoryProposalCard — the soft, in-character "Should I remember that?" consent
 * card (character-guided profiling, WU-W2). When an NPC `/talk` reply carries an
 * optional `memoryProposal`, this renders inline under the reply bubble with three
 * choices: Remember it 💫 / Not really / That's private.
 *
 * PRESENTATIONAL ONLY — no API calls, no local state. It renders the prompt + the
 * three buttons (idle / saving / error) or a collapsed terminal chip, and reports
 * the user's choice through `onDecide`. The parent (PlayerProfileOverlay) owns the
 * POST to `/api/profile/memories` + the optimistic state, so nothing is ever
 * written without an explicit button press (L-CONSENT).
 */

import type { MemoryProposal } from '../types/memory';

export type ProposalCardState =
  | 'idle'        // buttons live, awaiting a choice
  | 'saving'      // POST in flight — buttons disabled
  | 'remembered'  // confirmed → success chip + petal bloom
  | 'declined'    // rejected → "let that go"
  | 'private'     // rejected + suppressTopicFamily → "kept between us"
  | 'error';      // POST failed — buttons return + retry hint

interface MemoryProposalCardProps {
  proposal: MemoryProposal;
  state: ProposalCardState;
  /** status = the memory lifecycle target; suppress = "That's private" (tag-family mute). */
  onDecide: (status: 'confirmed' | 'rejected', suppressTopicFamily: boolean) => void;
}

const TERMINAL_COPY: Record<'remembered' | 'declined' | 'private', string> = {
  remembered: "💫 I'll remember that",
  declined: "Okay, I'll let that go.",
  private: '🔒 Kept between us',
};

export function MemoryProposalCard({ proposal, state, onDecide }: MemoryProposalCardProps) {
  // Collapsed, non-interactive terminal chip. `confirmed` plays the petal bloom once.
  if (state === 'remembered' || state === 'declined' || state === 'private') {
    const variant = state === 'remembered' ? 'confirmed' : state;
    return (
      <div
        className={`player-profile-npc-proposal player-profile-npc-proposal--chip ${variant}`}
        role="status"
      >
        <span className="player-profile-npc-proposal-chip-text">{TERMINAL_COPY[state]}</span>
      </div>
    );
  }

  const saving = state === 'saving';

  return (
    <div className="player-profile-npc-proposal">
      <span className="player-profile-npc-proposal-prompt">Should I remember that?</span>
      {proposal.text && (
        <span className="player-profile-npc-proposal-text">“{proposal.text}”</span>
      )}
      <div className="player-profile-npc-proposal-actions" role="group" aria-label="Remember this?">
        <button
          type="button"
          className="player-profile-npc-proposal-btn remember"
          disabled={saving}
          onClick={() => onDecide('confirmed', false)}
        >
          Remember it 💫
        </button>
        <button
          type="button"
          className="player-profile-npc-proposal-btn decline"
          disabled={saving}
          onClick={() => onDecide('rejected', false)}
        >
          Not really
        </button>
        <button
          type="button"
          className="player-profile-npc-proposal-btn private"
          disabled={saving}
          onClick={() => onDecide('rejected', true)}
        >
          That's private
        </button>
      </div>
      {state === 'error' && (
        <span className="player-profile-npc-proposal-hint">Didn't save — tap again.</span>
      )}
    </div>
  );
}
